import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import AppMUIAutocomplete from '../../../../../../components/common/AppMUIAutoComplete';
import AppTextInput from '../../../../../../components/common/AppTextInput';
import QUERY_KEYS from '../../../../../../constants/queryKeys';
import { useClientsContext } from '../../../../../../context/clients/ClientsContextProvider';
import { handleGetReservationZonesService } from '../../../../../../services/operations/reservationZones';
import { AnnouncementFormValues } from '../types';

type ReservationZoneOption = {
  value: string;
  title: string;
  color: string;
};

function ReservationDetails() {
  const { selectedClient, selectedLocation } = useClientsContext();
  const {
    control,
    watch,
    formState: { errors },
    trigger,
  } = useFormContext<AnnouncementFormValues>();

  const isClientSelected = selectedLocation?.isClient;
  const isBranchSelected = selectedLocation?.isBranch;
  const isSubBranchSelected = selectedLocation?.isSubBranch;

  const selectedBranchId: string[] = isClientSelected
    ? watch('branchIds')
    : isBranchSelected && selectedLocation?.id
      ? [selectedLocation.id]
      : isSubBranchSelected && selectedLocation?.branchId
        ? [selectedLocation.branchId]
        : [];
  const selectedSubBranchId: string[] = isBranchSelected
    ? watch('subBranchIds')
    : isSubBranchSelected && selectedLocation?.id
      ? [selectedLocation.id]
      : [];

  const { data: reservationZonesData, isLoading } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_RESERVATION_ZONES,
      selectedClient?._id,
      selectedBranchId,
      selectedSubBranchId,
    ],
    queryFn: () =>
      handleGetReservationZonesService({
        clientId: selectedClient?._id,
        ...(selectedBranchId.length > 0 && { branchId: selectedBranchId }),
        ...(selectedSubBranchId.length > 0 && {
          subBranchId: selectedSubBranchId,
        }),
      }),
    enabled: !!selectedClient?._id,
  });

  const reservationZoneOptions = React.useMemo<ReservationZoneOption[]>(
    () =>
      reservationZonesData?.reservationZones?.map((zone: any) => ({
        value: zone._id,
        title: zone.title,
        color: zone.color || '#E5E5E5',
      })) || [],
    [reservationZonesData],
  );

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Controller
        control={control}
        name="reservation_zone"
        render={({ field }) => {
          const selectedZone =
            reservationZoneOptions.find(
              (option) => option.value === field.value,
            ) || null;

          return (
            <AppMUIAutocomplete
              label="אזור הזמנה"
              size="small"
              options={reservationZoneOptions}
              fullWidth
              required
              loading={isLoading}
              noOptionsText="לא נמצאו אזורים"
              loadingText="טוען אזורים..."
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              value={selectedZone}
              onChange={(_, newValue) => {
                field.onChange(newValue?.value || '');
                trigger('reservation_zone');
              }}
              startAdornment={
                selectedZone ? (
                  <span
                    className="me-1 inline-block h-5 w-5 rounded-[2px] border border-black"
                    style={{ backgroundColor: selectedZone.color }}
                  />
                ) : undefined
              }
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;

                return (
                  <li key={option.value} {...optionProps}>
                    <div className="flex items-center gap-2 py-1">
                      <span
                        className="inline-block h-5 w-5 rounded-[2px] border border-black"
                        style={{ backgroundColor: option.color }}
                      />
                      <span>{option.title}</span>
                    </div>
                  </li>
                );
              }}
              error={!!errors.reservation_zone}
              helperText={errors.reservation_zone?.message}
            />
          );
        }}
      />
      <Controller
        control={control}
        name="number_of_people"
        render={({ field }) => (
          <AppTextInput
            {...field}
            type="number"
            inputMode="numeric"
            min={1}
            label="מספר סועדים"
            className="border-none"
            required
            error={errors.number_of_people?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="client_name"
        render={({ field }) => (
          <AppTextInput
            {...field}
            label="שם לקוח"
            className="border-none"
            required
            error={errors.client_name?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="phone"
        render={({ field }) => (
          <AppTextInput
            {...field}
            label="טלפון"
            className="border-none"
            required
            error={errors.phone?.message}
          />
        )}
      />
    </div>
  );
}

export default ReservationDetails;
